/**
 * Dialog for sending an onboarding questionnaire to this supplier.
 *
 * CR-8252 (decision 4): onboarding questionnaires are the one questionnaire
 * system, so this offers the published questionnaires from Supplier
 * Onboarding (it used to create a SupplierQualification from a qualification
 * template). One the supplier already holds, any version, cannot be picked.
 */

import { useState, useEffect } from 'react';
import { onboardingAPI, type OnboardingDataset } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Loader2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../utils/errorUtils';

interface AssignQuestionnaireDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  supplierId: number;
  supplierName: string;
  /** `${name}|${dataset_type}` of the questionnaires the supplier already holds. */
  heldKeys: Set<string>;
  onAssigned: () => void;
}

/**
 * Identifies a questionnaire across its versions (same name and type).
 *
 * @param name - questionnaire name
 * @param type - questionnaire type
 * @returns the key shared by every version
 */
export function questionnaireKey(name: string | undefined, type: string | undefined): string {
  return `${name ?? ''}|${type ?? ''}`;
}

/**
 * Lets a customer send one onboarding questionnaire to a supplier.
 */
export function AssignQuestionnaireDialog({
  open,
  onOpenChange,
  supplierId,
  supplierName,
  heldKeys,
  onAssigned,
}: AssignQuestionnaireDialogProps) {
  const [questionnaires, setQuestionnaires] = useState<OnboardingDataset[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setSelectedId('');
      return;
    }
    const load = async () => {
      setLoading(true);
      try {
        const res = await onboardingAPI.getDatasets({ is_active: true, page_size: 100 });
        const data = res.data.results || [];
        setQuestionnaires(data.filter((d) => d.is_active && d.is_published));
      } catch (err: unknown) {
        console.error('Failed to load questionnaires:', err);
        toast.error('Failed to load questionnaires');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [open]);

  const handleSubmit = async () => {
    if (!selectedId) return;

    setSubmitting(true);
    try {
      const res = await onboardingAPI.assignQuestionnaire(Number(selectedId), [supplierId]);
      if (res.data.created_count > 0) {
        toast.success(`Questionnaire sent to ${supplierName}`);
      } else {
        toast.info(`${supplierName} already has this questionnaire`);
      }
      onOpenChange(false);
      onAssigned();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Could not send the questionnaire'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Assign Questionnaire
          </DialogTitle>
          <DialogDescription>
            {supplierName} gets the questionnaire to complete, and you review their answers.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="questionnaire-select">Questionnaire</Label>
            {loading ? (
              <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading questionnaires...
              </div>
            ) : questionnaires.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">
                No published questionnaires yet. Publish one in Supplier Onboarding first.
              </p>
            ) : (
              <Select value={selectedId} onValueChange={setSelectedId}>
                <SelectTrigger id="questionnaire-select">
                  <SelectValue placeholder="Select a questionnaire..." />
                </SelectTrigger>
                <SelectContent>
                  {questionnaires.map((q) => {
                    const held = heldKeys.has(questionnaireKey(q.name, q.dataset_type));
                    return (
                      <SelectItem key={q.id} value={String(q.id)} disabled={held}>
                        {q.name}{held ? ' (already sent)' : ''}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!selectedId || submitting}
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Send
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
